// Firestore helper for fetching artform content
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Simple in-memory cache with TTL (5 minutes)
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Same artform may be stored under a different document id than the UI slug
 * (e.g. movement "Genre Scene" → genre-scene, but Firestore has genre-painting).
 * Try these ids in order after the primary key.
 */
const FIRESTORE_ARTFORM_ALIASES = {
  // UI slug from movement text vs doc id you chose in Firestore
  'genre-scene': ['genre-painting', 'genre_painting'],
  'ashcan-school': ['american-realism', 'social-realism'],
};

function artformDocIdsToTry(primaryKey) {
  const k = String(primaryKey || '').toLowerCase().trim();
  const list = [k, ...(FIRESTORE_ARTFORM_ALIASES[k] || [])];
  return [...new Set(list.filter(Boolean))];
}

function keyToQuery(key = '') {
  return String(key || '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** When slug→Title fails (404), try these Wikipedia page titles in order. Keys are normalized artform keys. */
const WIKIPEDIA_TITLE_FALLBACKS = {
  'genre-scene': ['Genre painting', 'Genre art'],
  'genre painting': ['Genre art'],
  'still-life': ['Still life'],
  'still life': ['Still life painting'],
  'history-painting': ['History painting'],
  'abstract-expressionism': ['Abstract expressionism'],
  'neo-expressionism': ['Neo-expressionism'],
};

function collectWikipediaTitlesForKey(normalizedKey = '') {
  const k = String(normalizedKey || '').toLowerCase().trim();
  const primary = keyToQuery(k);
  const extras = WIKIPEDIA_TITLE_FALLBACKS[k] || [];
  const out = [];
  const seen = new Set();
  for (const t of [primary, ...extras]) {
    const s = String(t || '').trim();
    if (!s) continue;
    const low = s.toLowerCase();
    if (seen.has(low)) continue;
    seen.add(low);
    out.push(s);
  }
  return out;
}

function splitIntoLearningSteps(text = '', maxSteps = 5) {
  const cleaned = String(text || '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return [];
  const parts = cleaned
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  /** Same progression as curated `artforms` docs (updateArtMovementKidContent.js). */
  const labels = [
    'What is it?',
    'How did it start?',
    'Key techniques',
    'Why is it important?',
    'Fun Facts',
  ];
  return parts.slice(0, maxSteps).map((body, i) => ({
    id: `web-step-${i + 1}`,
    label: labels[i] || `Step ${i + 1}`,
    body,
  }));
}

async function fetchArtformFromWeb(normalizedKey = '') {
  const titles = collectWikipediaTitlesForKey(normalizedKey);
  if (!titles.length) return null;

  for (const query of titles) {
    try {
      const encoded = encodeURIComponent(query);
      const url = `https://en.wikipedia.org/api/rest_v1/page/summary/${encoded}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) continue;
      const data = await res.json();
      const extract = String(data?.extract || '').trim();
      if (!extract) continue;

      const steps = splitIntoLearningSteps(extract, 5);
      return {
        title: data?.title || query,
        era: data?.description || '',
        steps,
        artists: [],
        famousWorks: [],
        source: 'wikipedia',
        sourceUrl: data?.content_urls?.desktop?.page || '',
        fetchedAt: Date.now(),
      };
    } catch (_) {
      /* try next title */
    }
  }
  return null;
}

/**
 * Fetches artform content from Firestore
 * @param {string} artformKey - The artform key (e.g., "impressionism", "cubism")
 * @returns {Promise<Object|null>} - The artform document data or null if not found
 */
export const getArtformContent = async (artformKey) => {
  if (!artformKey || typeof artformKey !== 'string') {
    console.warn('⚠️ Invalid artformKey provided:', artformKey);
    return null;
  }

  // Normalize the key (lowercase, trim)
  const normalizedKey = artformKey.toLowerCase().trim();

  // Check cache first
  const cached = cache.get(normalizedKey);
  if (cached) {
    const now = Date.now();
    if (now - cached.timestamp < CACHE_TTL) {
      console.log(`✅ Using cached artform content for: ${normalizedKey}`);
      return cached.data;
    } else {
      // Cache expired, remove it
      cache.delete(normalizedKey);
    }
  }

  try {
    const docIds = artformDocIdsToTry(normalizedKey);
    console.log(`🔄 Fetching artform content for: ${normalizedKey} (trying ids: ${docIds.join(', ')})`);
    console.log(`🔄 Using Firestore db instance:`, db ? 'db exists' : 'db is null');

    for (const docId of docIds) {
      const artformRef = doc(db, 'artforms', docId);
      const artformSnap = await getDoc(artformRef);
      if (artformSnap.exists()) {
        const data = artformSnap.data();
        if (docId !== normalizedKey) {
          console.log(`✅ Artform resolved via alias: artforms/${docId} (requested key: ${normalizedKey})`);
        } else {
          console.log(`✅ Artform data retrieved, keys:`, data ? Object.keys(data) : 'null');
        }
        cache.set(normalizedKey, { data, timestamp: Date.now() });
        return data;
      }
    }

    console.warn(`⚠️ No artforms document for any of: ${docIds.join(', ')}`);
    const webData = await fetchArtformFromWeb(normalizedKey);
    if (webData) {
      cache.set(normalizedKey, { data: webData, timestamp: Date.now() });
      console.log(`✅ Artform content loaded from Wikipedia fallback for: ${normalizedKey}`);
      return webData;
    }
    return null;
  } catch (error) {
    console.error(`❌ Error fetching artform content for ${normalizedKey}:`, error);
    console.error(`❌ Error details:`, {
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    return null;
  }
};

/**
 * Clears the cache for a specific artform or all artforms
 * @param {string|null} artformKey - The artform key to clear, or null to clear all
 */
export const clearArtformCache = (artformKey = null) => {
  if (artformKey) {
    const normalizedKey = artformKey.toLowerCase().trim();
    cache.delete(normalizedKey);
    console.log(`🗑️ Cache cleared for: ${normalizedKey}`);
  } else {
    cache.clear();
    console.log('🗑️ All artform cache cleared');
  }
};
